import React, { useMemo, useState } from 'react';
import { Alert, Button, Form, Input, Popconfirm, Select, Table, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { showError } from 'utils/exceptionUtils';
import {
  GET_TT_PRESORT_CONFIGS_WITH_PAGINATION,
  TtPresortConfigItem,
  TtPresortConfigPageResponse,
} from './graphql/queries/getTtPresortConfigs';
import {
  SAVE_TT_PRESORT_CONFIG,
  DELETE_TT_PRESORT_CONFIG,
  SaveTtPresortConfigInput,
} from './graphql/mutations/ttPresortConfigMutations';
import TtPresortConfigFormDrawer from './components/TtPresortConfigFormDrawer';

const BIZ_TYPE_OPTIONS = [
  { label: 'FBT', value: 'FBT' },
  { label: 'CBT', value: 'CBT' },
  { label: 'UNLIMITED', value: 'UNLIMITED' },
];

type FilterValues = {
  fromCode?: string;
  carrierCode?: string;
  ttBizType?: string;
  presortCode?: string;
};

const TtPresortConfigPage: React.FC = () => {
  const [filterForm] = Form.useForm<FilterValues>();
  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<TtPresortConfigItem | null>(null);

  const { data, loading, refetch } = useQuery<TtPresortConfigPageResponse>(
    GET_TT_PRESORT_CONFIGS_WITH_PAGINATION,
    {
      variables: {
        filter: {
          fromCode: appliedFilters.fromCode || undefined,
          carrierCode: appliedFilters.carrierCode || undefined,
          ttBizType: appliedFilters.ttBizType || undefined,
          presortCode: appliedFilters.presortCode || undefined,
          isDeleted: false,
        },
        pageRequest: { page: pagination.current, size: pagination.pageSize },
      },
      fetchPolicy: 'network-only',
    }
  );

  const [saveTtPresortConfig] = useMutation(SAVE_TT_PRESORT_CONFIG);
  const [deleteTtPresortConfig] = useMutation(DELETE_TT_PRESORT_CONFIG);

  const handleFilter = () => {
    setAppliedFilters(filterForm.getFieldsValue());
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    filterForm.resetFields();
    setAppliedFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showNewDrawer = () => {
    setSelectedRecord(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = (record: TtPresortConfigItem) => {
    setSelectedRecord(record);
    setDrawerMode('edit');
    setDrawerVisible(true);
  };

  const handleDrawerSubmit = async (values: SaveTtPresortConfigInput) => {
    try {
      await saveTtPresortConfig({ variables: { input: values } });
      message.success(drawerMode === 'create' ? '创建成功' : '更新成功');
      setDrawerVisible(false);
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTtPresortConfig({ variables: { id } });
      message.success('删除成功');
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const nowrap = (v: any) => <span style={{ whiteSpace: 'nowrap' }}>{v || v === 0 ? v : '-'}</span>;

  const columns: TableColumnsType<TtPresortConfigItem> = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, render: nowrap },
    { title: '始发地', dataIndex: 'fromCode', key: 'fromCode', render: nowrap },
    { title: '下一站', dataIndex: 'nextStopCode', key: 'nextStopCode', render: nowrap },
    { title: '尾程商', dataIndex: 'carrierCode', key: 'carrierCode', render: nowrap },
    { title: '业务类型', dataIndex: 'ttBizType', key: 'ttBizType', render: nowrap },
    { title: '预分拣码', dataIndex: 'presortCode', key: 'presortCode', render: nowrap },
    { title: '尾程三段码', dataIndex: 'mappedSortingCode', key: 'mappedSortingCode', render: nowrap },
    { title: '面单简称', dataIndex: 'labelCode', key: 'labelCode', render: nowrap },
    { title: '排序', dataIndex: 'sortOrder', key: 'sortOrder', render: nowrap },
    { title: '操作人', dataIndex: 'updatedBy', key: 'updatedBy', render: nowrap },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      render: (_: any, record: TtPresortConfigItem) => (
        <div className="flex gap-2">
          <Button type="link" style={{ paddingLeft: 0 }} onClick={() => showEditDrawer(record)}>编辑</Button>
          <Popconfirm title="确认删除该配置？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger style={{ paddingLeft: 0 }}>删除</Button>
          </Popconfirm>
        </div>
      ),
    },
  ], []);

  const tableData = data?.getTtPresortConfigsWithPagination?.content ?? [];
  const totalElements = data?.getTtPresortConfigsWithPagination?.totalElements ?? 0;

  const drawerInitialValues: SaveTtPresortConfigInput | undefined = selectedRecord
    ? {
        id: selectedRecord.id,
        fromCode: selectedRecord.fromCode,
        nextStopCode: selectedRecord.nextStopCode,
        carrierCode: selectedRecord.carrierCode,
        ttBizType: selectedRecord.ttBizType,
        presortCode: selectedRecord.presortCode,
        mappedSortingCode: selectedRecord.mappedSortingCode ?? undefined,
        labelCode: selectedRecord.labelCode ?? undefined,
        sortOrder: selectedRecord.sortOrder ?? undefined,
      }
    : undefined;

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="预分拣配置说明"
        description={
          <div className="text-xs leading-5">
            <div>用途：维护「始发地 + 下一站 + 尾程商 + 业务类型 → 预分拣码」的路由配置。</div>
            <div>使用场景：建包时据此展示可选「预分拣码」；选中后写入 bag.sorting_rule_second，参与装包/落格匹配；面单按「面单简称」目视分拣。</div>
            <div>
              前置 / 规则：① 依赖「基础配置」——始发地/下一站/尾程商需先在基础配置里存在（本页下拉数据源）；
              ② 五元组（始发地 + 下一站 + 尾程商 + 业务类型 + 预分拣码）唯一，重复会报错；
              ③ 尾程商为 SWIFTX 时必须填「尾程三段码」，否则无法建带码包牌；
              ④ 业务类型 UNLIMITED 对所有业务类型兜底可见。
            </div>
          </div>
        }
      />
      <Form form={filterForm} layout="inline" className="py-3 flex flex-wrap gap-2">
        <Form.Item name="fromCode" style={{ marginBottom: 0 }}>
          <Input placeholder="始发地" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="carrierCode" style={{ marginBottom: 0 }}>
          <Input placeholder="尾程商" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="ttBizType" style={{ marginBottom: 0 }}>
          <Select placeholder="业务类型" options={BIZ_TYPE_OPTIONS} allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="presortCode" style={{ marginBottom: 0 }}>
          <Input placeholder="预分拣码" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" onClick={handleFilter}>查询</Button>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button onClick={handleReset}>重置</Button>
        </Form.Item>
      </Form>

      <div className="flex justify-end mb-3">
        <Button type="primary" onClick={showNewDrawer}><span className="font-bold">新建</span></Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        tableLayout="auto"
        className="mb-4"
      />

      <TablePaginationBar
        total={totalElements}
        current={pagination.current}
        pageSize={pagination.pageSize}
        onChange={(page, size) => setPagination({ current: page, pageSize: size })}
      />

      <TtPresortConfigFormDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSubmit={handleDrawerSubmit}
        initialValues={drawerInitialValues}
        mode={drawerMode}
      />
    </div>
  );
};

export default TtPresortConfigPage;
